import React from "react";
import { useNavigate } from "react-router-dom";
import { get, startCase } from "lodash";
import {
  useTheme,
  Image,
  Paragraph,
  MtpCell,
  Button
} from "@tvg/design-system";
import { TalentPickData } from "@tvg/ts-types/TalentPicks";
import { UnaryFn } from "@tvg/ts-types/Functional";
import buildRaceUrl, { buildTalentPicksTVG5Url } from "@tvg/formatter/url";
import { VisualSelection } from "@tvg/ts-types/Selections";
import { WagerTypeCodesInvertedEnum } from "@tvg/ts-types/Wager";
import { RaceStatusType } from "@tvg/ts-types/Race";
import formatCurrency from "@tvg/formatter/currency";
import { useGetValidTalentPick } from "@tvg/talent-picks-desk/src/hooks/useGetValidTalentPick";

import { getVisualSelections } from "./helpers";
import BetSummary from "./BetSummary";
import {
  CardWrapper,
  CardHeader,
  AvatarWrapper,
  SummaryInfo,
  TalentSummaryInfo,
  RaceSummaryInfo,
  CardBody,
  ActionsWrapper,
  AmountContainer,
  AmountFlexWrapper,
  ButtonWrapper
} from "./styled-components";

interface TalentPickButton {
  type: string;
  qaLabel: string;
  label: UnaryFn<number, string>;
  onClick: UnaryFn<TalentPickData, void>;
}

interface Props {
  talentPick: TalentPickData;
  showRaceSummaryInfo?: boolean;
  showViewRaceButton?: boolean;
  showAddToBetslipButton?: boolean;
  qaLabel?: string;
  onAddToBetslip?: UnaryFn<TalentPickData, void>;
  numWagerableRunners?: string[];
  onHomePage?: boolean;
  bettingBtn?: TalentPickButton;
  isFDTVPage?: boolean;
}

const TalentPicksCard = ({
  showRaceSummaryInfo = true,
  showViewRaceButton = true,
  showAddToBetslipButton = true,
  talentPick,
  qaLabel = "talentPick-card",
  onAddToBetslip,
  numWagerableRunners,
  onHomePage = false,
  bettingBtn,
  isFDTVPage = false
}: Props) => {
  const { space } = useTheme();
  const navigate = useNavigate();

  const validTalentPick = useGetValidTalentPick({ onHomePage, talentPick });

  const {
    talent,
    mtp,
    raceStatus,
    track: trackCode,
    race: raceNumber,
    trackName,
    racePostTime,
    amount: wagerAmount,
    price: betTotal,
    wagerType
  } = talentPick;
  const { name: talentName, pictureUrl: talentPicture } = talent;
  const { abbreviation: wagerTypeCode, isKey, isBox, legCount } = wagerType;

  const handleViewRace = () => {
    navigate(buildRaceUrl(trackCode, trackName, raceNumber));
  };

  const handleAddToBetslip = () => {
    if (onAddToBetslip) {
      onAddToBetslip(talentPick);
    } else {
      navigate(buildTalentPicksTVG5Url(talentPick));
    }
  };

  const wagerTypeName = startCase(
    get(WagerTypeCodesInvertedEnum, wagerTypeCode)
      .replace("_", " ")
      .toLowerCase()
  );

  return (
    <CardWrapper data-qa-label={qaLabel} onHomePage={onHomePage}>
      <CardHeader
        space={space}
        onHomePage={onHomePage}
        onClick={() => {
          navigate(buildRaceUrl(trackCode, trackName, raceNumber));
        }}
        isFDTVPage={isFDTVPage}
      >
        <AvatarWrapper>
          <Image
            qaLabel="talent-avatar"
            source={talentPicture}
            width={44}
            height={44}
            resizeMode="cover"
          />
        </AvatarWrapper>
        {showRaceSummaryInfo && (
          <SummaryInfo space={space} data-qa-label="race-summary-info">
            <TalentSummaryInfo>
              <Paragraph qaLabel="talent-name">{talentName}</Paragraph>
              <AmountFlexWrapper>
                <AmountContainer>
                  <Paragraph
                    qaLabel="bet-amount-n-type"
                    fontFamily="regular"
                    color="grey.800"
                    mr="space-1"
                  >{`${formatCurrency(
                    wagerAmount
                  )} ${wagerTypeCode}`}</Paragraph>
                </AmountContainer>
                <AmountContainer>
                  <Paragraph
                    qaLabel="bet-total-n-type"
                    fontFamily="regular"
                    color="grey.800"
                    ml="space-1"
                  >
                    {`${formatCurrency(betTotal)} Ticket`}
                  </Paragraph>
                </AmountContainer>
              </AmountFlexWrapper>
            </TalentSummaryInfo>
            <RaceSummaryInfo>
              <MtpCell
                mtp={+mtp}
                status={raceStatus as RaceStatusType["code"]}
                trackCode={trackCode}
                raceNumber={raceNumber?.toString()}
                postTime={racePostTime}
                withBackground={false}
                align="right"
                showMtp
              />
            </RaceSummaryInfo>
          </SummaryInfo>
        )}
      </CardHeader>
      <CardBody space={space}>
        <BetSummary
          wagerAmount={wagerAmount}
          betTotal={betTotal}
          selections={
            validTalentPick?.visualSelections ??
            (getVisualSelections(
              talentPick?.selections,
              talentPick?.raceType
            ) as Array<Array<VisualSelection>>)
          }
          wagerTypeCode={wagerTypeCode}
          wagerTypeName={wagerTypeName}
          isKey={isKey}
          isBox={isBox}
          legCount={legCount}
          numWagerableRunners={numWagerableRunners}
          raceNumber={raceNumber?.toString()}
          onHandicaping
        />
      </CardBody>
      <ActionsWrapper space={space} onHomePage={onHomePage}>
        {showViewRaceButton && (
          <ButtonWrapper onHomePage={onHomePage} isViewRaceBtn>
            <Button
              qaLabel="view-race-button"
              variant="secondary"
              onPress={handleViewRace}
              isStretched
            >
              View Race
            </Button>
          </ButtonWrapper>
        )}
        {showAddToBetslipButton && (
          <ButtonWrapper>
            <Button
              qaLabel="add-to-betslip-button"
              variant="betting"
              onPress={handleAddToBetslip}
              isStretched
            >
              Add to Betslip
            </Button>
          </ButtonWrapper>
        )}
        {onHomePage && (
          <ButtonWrapper onHomePage={onHomePage} isBettingBtn>
            <Button
              variant="betting"
              onPress={() => bettingBtn && bettingBtn.onClick(talentPick)}
              qaLabel="create-bet-button"
              mb="space-4"
              isStretched
            >
              CREATE TICKET
            </Button>
          </ButtonWrapper>
        )}
      </ActionsWrapper>
    </CardWrapper>
  );
};

export default TalentPicksCard;
